/*
 * Copyright (c) 2022. Nedaware SL
 */

import {BasicEntity, BasicProperty} from '../../basic-entity-back/annotations';
import {Persona} from './persona.model';
import {PropertyType} from '../../basic-entity-back/property-type/property-type';
import {ManagerCachingStrategy, ReadWrite, SearchType} from '../../basic-entity-back/basic-entity-interface/mapping-external';
import {RangeFilters} from '../../basic-entity-back/filters/range-filters';
import {NumericFilter} from '../../basic-entity-back/filters/numeric-filter';
import {BooleanFilter} from '../../basic-entity-back/filters/boolean-filter';
import {PersonaContacto} from "./contacto.model";
import {NestedBehavior} from "../../basic-entity-back/property-type/nested-model-type";
import {DescuentoCliente} from "../trabajos/descuento-cliente.model";
import {PrioridadEnvio} from "../envios/prioridad-envio.model";
import {ClienteAsociado} from "./cliente-asociado.model";

@BasicEntity({
    name: 'Clientes',
    endpoint: 'clientes',
    isPaginated: true,
    paginationSizes: [20, 30, 50],
    modelNameProperty: 'nombreCompleto',
    acceptsBulkIriSearch: true,
    type: 'Cliente',
    managerCaching: ManagerCachingStrategy.NoCache
})

export class Cliente extends Persona {
    @BasicProperty({
        name: 'Nombre / Razón Social',
        type: PropertyType.String(),
        sortable: true
    })
    private _denominacionSocial;

    @BasicProperty({
        name: 'Empresa',
        type: PropertyType.Boolean(),
        filters: [BooleanFilter],
    })
    private _empresa: boolean = false;

    @BasicProperty({
        name: 'Intracomunitaria',
        type: PropertyType.Boolean(),
        searchable: SearchType.Boolean
    })
    private _intracomunitaria: boolean = false;

    @BasicProperty({
        name: 'Saldo',
        type: PropertyType.NestedModel(PersonaContacto, undefined, NestedBehavior.CreateOnly),
        array: true
    })
    private _contactos;

    @BasicProperty({
        name: 'Prioridad para envío',
        type: PropertyType.NestedModel(PrioridadEnvio, null, NestedBehavior.SendIriOnly),
        order: 19
    })
    private _prioridadEnvio: PrioridadEnvio;

    @BasicProperty({
        name: 'Descuentos por método de fabricación',
        tag: 'Descuentos',
        type: PropertyType.NestedModel(DescuentoCliente),
        array: true
    })
    private _descuentosMetodoFabricacion = [];

    @BasicProperty({
        name: 'Activado',
        type: PropertyType.Boolean(),
        order: 10
    })
    private _activado: boolean = false;

    // Lo que lee el guard de la sección de asociados (Tarea 9). ReadOnly porque la escritura es
    // sólo de empleado en la API (grupo 'clientes_in_role_empleado'): un cliente que la mandara en
    // su propio PUT ya la vería ignorada por la API, pero marcarla aquí es más honesto -evita que
    // se lea como un campo editable desde este proyecto- y quita ese campo del cuerpo del PUT.
    @BasicProperty({
        name: 'Puede gestionar clientes asociados',
        type: PropertyType.Boolean(),
        readWrite: ReadWrite.ReadOnly
    })
    private _esPadre: boolean = false;

    // De sólo lectura a propósito: la API no acepta escribir asociados anidados en la ficha del
    // cliente. Las altas y ediciones van contra /cliente_asociados, mandando 'cliente' con el IRI
    // del cliente de la sesión. De aquí sale la lista de direcciones del grupo para el desplegable
    // de dirección de entrega (Tarea 10).
    @BasicProperty({
        name: 'Clientes asociados',
        tag: 'Clientes asociados',
        type: PropertyType.NestedModel(ClienteAsociado),
        array: true,
        readWrite: ReadWrite.ReadOnly
    })
    private _clientesAsociados;

    get denominacionSocial() {
        return this._denominacionSocial;
    }

    set denominacionSocial(value) {
        this._denominacionSocial = value;
    }


    get intracomunitaria(): boolean {
        return this._intracomunitaria;
    }

    set intracomunitaria(value: boolean) {
        this._intracomunitaria = value;
    }


    get empresa(): boolean {
        return this._empresa;
    }

    set empresa(value: boolean) {
        this._empresa = value;
    }

    get contactos() {
        return this._contactos;
    }

    set contactos(value) {
        this._contactos = value;
    }

    get datosCliente(): string {
        let segundaLinea = '';
        if (this.email) {
            segundaLinea = `${this.email}`;
        }
        return segundaLinea;
    }

    get matIcon(): string {
        if (this.empresa) {
            return 'business';
        }
        return 'person';
    }

    get matIconColor(): string {
        if (this.usuario) {
            if (this.usuario.enabled) {
                return 'primary';
            }
            return 'black';
        }
    }

    get prioridadEnvio(): PrioridadEnvio {
        return this._prioridadEnvio;
    }

    set prioridadEnvio(value: PrioridadEnvio) {
        this._prioridadEnvio = value;
    }

    get descuentosMetodoFabricacion() {
        return this._descuentosMetodoFabricacion;
    }

    set descuentosMetodoFabricacion(value) {
        this._descuentosMetodoFabricacion = value;
    }

    get activado(): boolean {
        return this._activado;
    }
    set activado(value: boolean) {
        this._activado = value;
    }

    get esPadre(): boolean {
        return this._esPadre;
    }

    set esPadre(value: boolean) {
        this._esPadre = value;
    }

    get clientesAsociados() {
        return this._clientesAsociados;
    }

    set clientesAsociados(value) {
        this._clientesAsociados = value;
    }
}

DescuentoCliente.MAP_CLIENTE.type = PropertyType.Uri(Cliente);
ClienteAsociado.CLIENTE_MAP.type = PropertyType.Uri(Cliente);
